'use client'

import { doAllAudioAnalysis } from '@/lib/audioprocessing-client-utils';
import { dataUrlToAudioBuffer, readFileAsArrayBuffer_clientSide } from '@/lib/client-utils';
import { MIN_AUDIO_DURATION } from '@/lib/config';
import { pickRandom } from '@/lib/maths-utils';
import { Switch } from '@headlessui/react';
import { log } from '@logtail/next';
import { useDebounceEffect } from 'ahooks';
import { AudioAnalysisData, AudioBeat, AudioEvent } from 'app/api/audioInput/lib';
import AspectRatioToggleWithAtom from 'app/components/AspectRatioToggleWithAtom';
import FilterableListbox from 'app/components/FilterableListbox';
import { FilterableListItem, styleOrPresetToFilterableListItem } from 'app/components/FilterableListbox-utils';
import { AudioPreProcessingConfig } from 'app/components/PresetLab';
import StyledSpinner from 'app/components/StyledSpinner';
import { allPresetsAtom, animationTypeAtom, aspectRatioAtom, audioBeatDataAtom, audioEventDataAtom, audioFilenameAtom, audioLocalFilenameAtom, audioPresetDataAtom, audioProcessingConfigsAtom, audioSegmentAnalysisAtom, audioSegmentEndAtom, audioSegmentStartAtom, audioSyncMotionPresetAtom, audioTotalDurationAtom, audioUrlAtom, backendOverrideAtom, classicMotionPresetAtom, duration30sAtom, fpsAtom, framewiseDataSeriesAtom, gpuFarmOverrideAtom, imagePromptAtom, imagePromptNsfwScoreAtom, imagePromptThumbnailAtom, imagePromptWeightAtom, isEstimatingTempoAtom, isMobileAudioEditorOpenAtom, librosaEventsAtom, parsedSettingsOverridesAtom, promptAtom, requiredAudioFieldsAtom, settingsOverridesAtom, slowMoAtom, smoothingAtom, styleAtom, submittingNewAnimationAtom } from 'app/components/atoms/animationFormAtoms';
import { DeforumStudioBackend, Message } from 'app/types';
import { Alert, Button, Checkbox, Dropdown, Label, Spinner, Textarea, Tooltip } from 'flowbite-react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { classNames } from 'lib/generic-utils';
import { GpuFarm, GpuFarmName } from 'lib/gpu/GpuFarm';
import _, { isNumber } from 'lodash';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import Dropzone from 'react-dropzone';
import { AiFillCloseSquare, AiOutlineInfoCircle } from 'react-icons/ai';
import { CiRedo } from "react-icons/ci";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { HiOutlineChevronUpDown } from 'react-icons/hi2';
import { LuImagePlus, LuMusic } from "react-icons/lu";
import { PiCoinVerticalBold, PiWaveformThin } from "react-icons/pi";
import ModalImage from 'react-modal-image';
import { useEffectOnce } from 'react-use';
import SparkMD5 from 'spark-md5';
import { AnimationCreationFailure, AnimationCreationResult, AnimationCreationSuccess, AspectRatio, Style } from './CreateAnimationTypes';
import MotionTypeSelector from './MotionTypeSelector';
import { analyzeAudioFile, checkIfAudioFileAreadyUploaded, checkIfImageFileAreadyUploaded, checkImageForNSFW, checkPrompt, createAnimationFromButton, getAudioUploadUrl, getImageUploadUrl } from './ServerActions';
import { calculateCost } from './animate-utils-client-safe';
import animateStyles from './animate.module.css';

export type AnimationFormData = {
    // General form state
    prompt:string,
    style:string,
    classicMotionPreset:string,
    audioSyncMotionPreset:string,
    aspectRatio:AspectRatio,
    
    // Classic-mode only form state
    slowMo:boolean,
    duration30s:boolean,
    
    // Hint to tell the user that the prompt is blank because it's hidden by the author
    isPromptHidden:boolean,

    // ATK-only form state 
    smoothing:number,

    // Image prompt form state
    useImagePrompt:boolean,
    imagePrompt?:string,
    imagePromptWeight:string,
    imagePromptNsfwScore?:string,
    imagePromptThumbnail?:string,
    
    // Audio-related form state
    isAudioReactive:boolean,
    audioLocalFilename?:string,
    audioUrl?:string,
    audioSegmentStart?:number,
    audioSegmentEnd?:number,
    audioSegmentAnalysis?:AudioAnalysisData,
    audioEventData?:AudioEvent[],
    audioBeatData?:AudioBeat,
    audioFilename?:string,
    audioProcessingConfigs?:AudioPreProcessingConfig[],
    
    // Admin overrides state
    settingsOverrides?:string,
    gpuFarmOverride?:GpuFarmName,
    backendOverride?:DeforumStudioBackend,
    audioPresetData?:string,

    framewiseDataSeries?:Record<string, number[]>
}

type AnimationFormProps = {
    enableAudioSync: boolean,
    is_admin: boolean,
    isAnonymous: boolean,
    initialFormData: AnimationFormData,
    styles: Style[],
    basedOn?: string,
    maxAudioDurationForUser: number,
    onSubmitStarted?: () => void
    onSubmitResponded?: (result: AnimationCreationResult | undefined) => void
    onAspectRatioChanged?: (aspectRatio: AspectRatio) => void
}

const MotionPresetsTip = () => {
    return (
        <div className="flex items-center my-4 text-sm">
            <AiOutlineInfoCircle className="inline-block mr-2 text-xs" />
            <Link href="/help" className="underline">
                Help
            </Link>
        </div>
    );
};

export default function AnimationForm({ enableAudioSync, is_admin, isAnonymous, initialFormData, styles, basedOn, onSubmitStarted, onSubmitResponded, maxAudioDurationForUser }: AnimationFormProps) {

    const presets = useAtomValue(allPresetsAtom);
    const styleList : FilterableListItem[] = styles.sort().map(styleOrPresetToFilterableListItem);
    const audioSyncPresetList : FilterableListItem[] = presets.sort().filter(preset => preset.isAudioReactive).map(styleOrPresetToFilterableListItem);
    const classicPresetList : FilterableListItem[] = presets.sort().filter(preset => !preset.isAudioReactive).map(styleOrPresetToFilterableListItem);

    styleList.unshift('(Random)');
    audioSyncPresetList.unshift('(Random)');
    classicPresetList.unshift('(Random)');

    // Non-persistent UI states that are not required by any other component
    const [uploadingAudio, setUploadingAudio] = useState(false);
    const [uploadingImagePrompt, setUploadingImagePrompt] = useState(false);
    const [showSettingsOverrides, setShowSettingsOverrides] = useState(false);
    const [dirty, setDirty] = useState(false); // Derived from form state
    const [creditCost, setCreditCost] = useState(-1); // Derived from form state
    const [submissionResult, setSubmissionResult] = useState<AnimationCreationResult | undefined>(undefined); // TODO could be moved to atom for easier sharing with parent.
    
    // We use these flags to track whether to hide the selected values and just show 'random' instead.
    // Under the covers, we select values as soon as 'random' is selected because we need to derive some info from the preset.
    const [styleWasRandomlySelected, setStyleWasRandomlySelected] = useState<boolean>(false);
    const [classicPresetWasRandomlySelected, setClassicPresetWasRandomlySelected] = useState<boolean>(false);
    const [audioSyncedPresetWasRandomlySelected, setAudioSyncedPresetWasRandomlySelected] = useState<boolean>(false);

    // Non persistent messages
    const [promptMessage, setPromptMessage] = useState('');
    const [uploadMessage, setUploadMessage] = useState<Message | undefined>();
    const [settingsOverridesMessage, setSettingsOverridesMessage] = useState<Message | undefined>();

    /////////////////////////////////////////////////////////////////////////////////////////
    // Form state is persisted client side as atoms. 
    // Here we prepare the atom set required by the UI
    /////////////////////////////////////////////////////////////////////////////////////////
    const [prompt, setPrompt] = useAtom(promptAtom);
    
    const [style, setStyle] = useAtom(styleAtom);
    const [classicMotionPreset, setClassicMotionPreset] = useAtom(classicMotionPresetAtom);
    const [audioSyncMotionPreset, setAudioSyncMotionPreset] = useAtom(audioSyncMotionPresetAtom);
    const [aspectRatio, setAspectRatio] = useAtom(aspectRatioAtom);
    
    // Classic-mode only form state
    const [slowMo, setSlowMo] = useAtom(slowMoAtom);
    const [duration30s, setDuration30s] = useAtom(duration30sAtom);

    // ATK-only (currently == audio-only) form state
    const [smoothing, setSmoothing] = useAtom(smoothingAtom);

    // Image prompt form state
    const [imagePrompt, setImagePrompt] = useAtom(imagePromptAtom);
    const [imagePromptWeight, setImagePromptWeight] = useAtom(imagePromptWeightAtom);
    const [imagePromptNsfwScore, setImagePromptNsfwScore] = useAtom(imagePromptNsfwScoreAtom);
    const [imagePromptThumbnail, setImagePromptThumbnail] = useAtom(imagePromptThumbnailAtom);
    
    // Audio-related form state
    const [animationType, setAnimationType] = useAtom(animationTypeAtom);
    const [audioLocalFilename, setAudioLocalFilename] = useAtom(audioLocalFilenameAtom);
    const [audioUrl, setAudioUrl] = useAtom(audioUrlAtom);
    const [audioSegmentStart, setAudioSegmentStart] = useAtom(audioSegmentStartAtom);
    const [audioSegmentEnd, setAudioSegmentEnd] = useAtom(audioSegmentEndAtom);
    const [audioEventData, setAudioEventData] = useAtom(audioEventDataAtom);
    const [audioBeatData, setAudioBeatData] = useAtom(audioBeatDataAtom);
    const [audioFilename, setAudioFilename] = useAtom(audioFilenameAtom);
    const [audioProcessingConfigs, setAudioProcessingConfigs] = useAtom(audioProcessingConfigsAtom);

    // Admin overrides state
    const [settingsOverrides, setSettingsOverrides] = useAtom(settingsOverridesAtom);
    const [gpuFarmOverride, setGpuFarmOverride] = useAtom(gpuFarmOverrideAtom);
    const [backendOverride, setBackendOverride] = useAtom(backendOverrideAtom);
    const [audioPresetData, setAudioPresetData] = useAtom(audioPresetDataAtom);

    // Transient state – this is state shared by components but not persisted to the DB 
    const setIsEstimatingTempo = useSetAtom(isEstimatingTempoAtom);
    const audioTotalDuration = useAtomValue(audioTotalDurationAtom);
    const [submittingNewAnimation, setSubmittingNewAnimation] = useAtom(submittingNewAnimationAtom);
    const [framewiseDataSeries, setFramewiseDataSeries] = useAtom(framewiseDataSeriesAtom);
    const [parsedSettingsOverride] = useAtom(parsedSettingsOverridesAtom);
    const setLibrosaEvents = useSetAtom(librosaEventsAtom);

    // Derived atom state
    const [fps] = useAtom(fpsAtom);
    const [requiredAudioFields] = useAtom(requiredAudioFieldsAtom);

    const [isMobile, setIsMobile] = useState(false);
    const [isMobileAudioEditorOpen, setIsMobileAudioEditorOpen] = useAtom(isMobileAudioEditorOpenAtom);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768 || window.innerHeight < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    /////////////////////////////////////////////////////////////////////////////////////////
    // Hydrate initial state into the atoms for form from server-provided data.
    /////////////////////////////////////////////////////////////////////////////////////////    
    useHydrateAtoms([
        [promptAtom, initialFormData.prompt],
        [styleAtom, initialFormData.style],
        [classicMotionPresetAtom, initialFormData.classicMotionPreset],
        [audioSyncMotionPresetAtom, initialFormData.audioSyncMotionPreset],
        [aspectRatioAtom, initialFormData.aspectRatio],
        [slowMoAtom, initialFormData.slowMo],
        [duration30sAtom, initialFormData.duration30s],
        [smoothingAtom, initialFormData.smoothing],
        [imagePromptAtom, initialFormData.imagePrompt||''],
        [imagePromptWeightAtom, initialFormData.imagePromptWeight],
        [imagePromptNsfwScoreAtom, initialFormData.imagePromptNsfwScore],
        [imagePromptThumbnailAtom, initialFormData.imagePromptThumbnail],
        [animationTypeAtom, initialFormData.isAudioReactive ? 'audio-reactive' : 'classic'],
        [audioLocalFilenameAtom, initialFormData.audioLocalFilename],
        [audioUrlAtom, initialFormData.audioUrl],
        [audioSegmentStartAtom, initialFormData.audioSegmentStart],
        [audioSegmentEndAtom, initialFormData.audioSegmentEnd],
        [audioSegmentAnalysisAtom, initialFormData.audioSegmentAnalysis],
        [audioEventDataAtom, initialFormData.audioEventData],
        [audioBeatDataAtom, initialFormData.audioBeatData],
        [audioFilenameAtom, initialFormData.audioFilename],
        [audioProcessingConfigsAtom, initialFormData.audioProcessingConfigs||[]],
        [settingsOverridesAtom, initialFormData.settingsOverrides],
        [gpuFarmOverrideAtom, initialFormData.gpuFarmOverride],
        [backendOverrideAtom, initialFormData.backendOverride],
        [audioPresetDataAtom, initialFormData.audioPresetData]
    ]);

    /////////////////////////////////////////////////////////////////////////////////////////

    // TODO: I wish there was another way to do this. It looks like we need to duplicate the rehydration
    // logic here because else the atoms are not updated when the user navigates to another animation
    // within the same tab. dangerouslyForceHydrate does NOT solve this because it forces a rehydrate on every
    // component reload, so completely blocks the UI (every edit causes a reload from initial state). 
    useEffectOnce(() => {

        unstable_batchedUpdates(() => {
            setPrompt(initialFormData.prompt);
            setStyle(initialFormData.style);
            setClassicMotionPreset(initialFormData.classicMotionPreset);
            setAudioSyncMotionPreset(initialFormData.audioSyncMotionPreset);
            setAspectRatio(initialFormData.aspectRatio);
            setSlowMo(initialFormData.slowMo);
            setDuration30s(initialFormData.duration30s);
            setSmoothing(initialFormData.smoothing);
            setImagePrompt(initialFormData.imagePrompt||'');
            setImagePromptWeight(initialFormData.imagePromptWeight);
            setImagePromptNsfwScore(initialFormData.imagePromptNsfwScore);
            setImagePromptThumbnail(initialFormData.imagePromptThumbnail);
            setAudioLocalFilename(initialFormData.audioLocalFilename);
            //TODO - need better distinction between data URL and remote URL. Here it will be a remote URL.
            setAudioUrl(initialFormData.audioUrl);
            setAudioSegmentStart(initialFormData.audioSegmentStart);
            setAudioSegmentEnd(initialFormData.audioSegmentEnd);
            setAudioEventData(initialFormData.audioEventData);
            setAudioBeatData(initialFormData.audioBeatData);
            setAudioFilename(initialFormData.audioFilename);
            setSettingsOverrides(initialFormData.settingsOverrides);
            setGpuFarmOverride(initialFormData.gpuFarmOverride);
            setBackendOverride(initialFormData.backendOverride);
            setAudioPresetData(initialFormData.audioPresetData);
            setAnimationType(initialFormData.isAudioReactive ? 'audio-reactive' : 'classic');
            setSubmittingNewAnimation(false);
            
            setAudioProcessingConfigs(initialFormData.audioProcessingConfigs||[]);
            setFramewiseDataSeries({}); // Will be derived. TODO - should this be stored differently given it's derived data?
        });

        // Immediately pick values if user wants random. Use flag to track whether the user selected random or not.
        // If a clean form is loaded, this will be applied immediately because random is the default.
        // This is because we need to derive some info from the preset, even if it was randomly selected. So we can't wait for
        // the user to submit the form to do the random pick.
        pickRandomValues();
    });
    
    const isAudioReactive = animationType === 'audio-reactive';

    const pickRandomValues = useCallback(() => {
        if (style === '(Random)') {
            const random = pickRandom(styles).name;
            log.debug(`Picked random style: ${random}`);
            unstable_batchedUpdates(() => {
                setStyle(random);
                setStyleWasRandomlySelected(true);
            });
        }

        if (presets.length === 0) {
            // Presets are not yet initialised.
            return;
        }

        if (classicMotionPreset === '(Random)') {
            const random = pickRandom(presets.filter(p => !p.isAudioReactive)).displayName!;
            log.debug(`Picked classic preset: ${random}`);
            unstable_batchedUpdates(() => {
                setClassicMotionPreset(random);
                setClassicPresetWasRandomlySelected(true);
            });
        }

        if (audioSyncMotionPreset === '(Random)') {
            const random = pickRandom(presets.filter(p => p.isAudioReactive && !p.clientSafeSettings.requiredAudioFields?.includes('event'))).displayName!;
            log.debug(`Picked audioSync preset: ${random}`);
            unstable_batchedUpdates(() => {
                setAudioSyncMotionPreset(random);
                setAudioSyncedPresetWasRandomlySelected(true);
            });
        }


    }, [setStyle, styles, presets, setClassicMotionPreset, setAudioSyncMotionPreset, style, classicMotionPreset, audioSyncMotionPreset])

    useEffect(() => {
        pickRandomValues();
    }, [pickRandomValues])



    const uploadImage = useCallback(async (file: File) => {

        const fileData = await readFileAsArrayBuffer_clientSide(file);
        const md5 = SparkMD5.ArrayBuffer.hash(fileData);
        const extension = file.name.split('.').pop() ?? '';
        const filename = `${md5}.${extension}`;
        if (await checkIfImageFileAreadyUploaded(md5, file.name)) {
            setImagePrompt(filename);
            setUploadMessage({
                message: "Image recognised from previous upload.",
                type: 'success',
                timestamp: Date.now(),
            });
            return;
        }

        // TODO: dodgy that we are using the md5 as computed client-side for the 
        // content-addressable filename. Theoretically an attacker could store a bogus audio file
        // under a range of incorrect MD5s, thereby poisonning the cache (why they'd do this is anyone's guess).
        // The solution is to use a CloudFlare worker to respond to upload events, calculate the MD5 in the worker, and move the file.
        const uploadUrl = await getImageUploadUrl(md5, extension);
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type || `image/${extension}`,
            },
        });

        if (!response.ok) {
            setImagePrompt('');
            let message;
            try {
                message = await response.text();
            } catch {
                // ignore
            }
            setUploadMessage({
                message: "Image upload failed: " + message ?? response.status,
                type: 'failure',
                timestamp: Date.now(),
            });
            return;
        }

        // Trigger NSFW check
        setUploadMessage({
            message: "Checking for NSFW content...",
            type: 'success',
            timestamp: Date.now(),
        });
        const nsfwResult = await checkImageForNSFW(filename);
        setImagePromptNsfwScore(nsfwResult?.nsfwScore?.toFixed(6));
        if (nsfwResult.isNsfw) {
            setImagePrompt('');
            setUploadMessage({
                message: "Likely NSFW. Try another image.",
                type: 'failure',
                timestamp: Date.now(),
            });
            return;
        }

        setImagePrompt(filename);
        setUploadMessage({
            message: "Image uploaded.",
            type: 'success',
            timestamp: Date.now(),
        });

    }, [setImagePrompt, setImagePromptNsfwScore]);

    const onDropImage = useCallback((acceptedFiles: File[]) => {
        // Assuming only one image is accepted
        const file = acceptedFiles[0];

        if (!file) {
            setUploadMessage({
                message: "Couldn't upload image. Ensure it's under 4.5MB and is a jpg, png or webp.",
                type: 'failure',
                timestamp: Date.now(),
            });
        }

        // Generate a preview thumbnail
        const reader = new FileReader();
        reader.onload = () => {
            setImagePromptThumbnail(reader.result as string);
        };
        reader.readAsDataURL(file);

        setUploadingImagePrompt(true);
        uploadImage(file)
            .then(() => { })
            .catch((error : unknown) => {
                log.error("Browser error: image upload failed", {error});
            })
            .finally(() => {
                setUploadingImagePrompt(false);
            });
    }, [setImagePromptThumbnail, uploadImage]);

    const uploadAudio = useCallback(async (file: File) => {

        try {
            const fileData = await readFileAsArrayBuffer_clientSide(file);
            const md5 = SparkMD5.ArrayBuffer.hash(fileData);
            if (await checkIfAudioFileAreadyUploaded(md5)) {
                setAudioFilename(`${md5}.mp3`);
                setUploadMessage({
                    message: "Audio recognised from previous upload.",
                    type: 'success',
                    timestamp: Date.now(),
                });
                return;
            }

            // TODO: dodgy that we are using the md5 as computed client-side for the 
            // content-addressable filename. Theoretically an attacker could store a bogus audio file
            // under a range of incorrect MD5s, thereby poisonning the cache (why they'd do this is anyone's guess).
            // The solution is to use a CloudFlare worker to respond to upload events, calculate the MD5 in the worker, and move the file.
            const uploadUrl = await getAudioUploadUrl(md5);
            const response = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type || 'audio/mpeg',
                },
            });

            if (!response.ok) {
                setAudioBeatData(undefined);
                setAudioUrl(undefined);
                let message;
                try {
                    message = await response.text();
                } catch {
                    // Deliberately ignore failure to retrieve error message
                }
                setUploadMessage({
                    message: "Audio upload failed: " + message ?? response.status,
                    type: 'failure',
                    timestamp: Date.now(),
                });
                return;
            }

            const filename = `${md5}.mp3`;
            setAudioFilename(filename);
            setUploadMessage({
                message: "Audio uploaded.",
                type: 'success',
                timestamp: Date.now(),
            });

        } catch (error) {
            log.error("Browser error: audio upload failed", {error});
            setUploadMessage({
                message: "Upload failed.",
                type: 'failure',
                timestamp: Date.now(),
            });
        }


    }, [setAudioBeatData, setAudioFilename, setAudioUrl]);

    // TODO  - deduplicate with onDropImage
    const onDropAudio = useCallback( async (acceptedFiles: File[]) => {
        // Assuming only one file is accepted
        const file = acceptedFiles[0];

        if (!file || file.size > 20 * 1024 * 1024) {
            setUploadMessage({ message: "Couldn't upload audio. Ensure it's under 20MB and is a supported format.", type: 'failure', timestamp: Date.now() });
            return;            
        }

        const audioContext = new AudioContext();
        const arrayBuffer = await file.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        if (audioBuffer.duration < MIN_AUDIO_DURATION) {
            setUploadMessage({ message: `Audio too short. Min: ${MIN_AUDIO_DURATION}s`, type: 'failure', timestamp: Date.now() });
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            // TODO - need better distinction between data URL and remote URL. Here it will be a data URL (very long string)
            // Skipping this set here is tricky because then we need to retrieve the final URL from the server after upload 
            // which raises some permission problems: how can we tell the user is allowed to access the remote audio if it is not yet
            // associated with a job?
            setAudioUrl(reader.result as string);
            setAudioLocalFilename(file.name);
        };
        reader.readAsDataURL(file);

        setUploadingAudio(true);
        uploadAudio(file)
            .then(() => { })
            .catch((error : unknown) => {
                log.error("Browser error: audio upload failed", {error});
                setUploadMessage({ message: "Upload failed.", type: 'failure', timestamp: Date.now() });
            })
            .finally(() => {
                setUploadingAudio(false);
            });
    }, [setAudioLocalFilename, setAudioUrl, uploadAudio]);


    useEffect(() => {
        const isDirty = (prompt !== initialFormData.prompt
            || aspectRatio.label !== initialFormData.aspectRatio?.label
            || style !== initialFormData.style
            || audioSyncMotionPreset !== initialFormData.audioSyncMotionPreset
            || classicMotionPreset !== initialFormData.classicMotionPreset
            || slowMo !== initialFormData.slowMo
            || imagePromptWeight !== initialFormData.imagePromptWeight
            || imagePrompt !== initialFormData.imagePrompt
            || audioFilename !== initialFormData.audioFilename
            || animationType !== (initialFormData.isAudioReactive ? 'audio-reactive' : 'classic')
            || duration30s !== !!initialFormData.duration30s
            || settingsOverrides !== initialFormData.settingsOverrides
            || audioPresetData !== initialFormData.audioPresetData
            || smoothing !== initialFormData.smoothing
            || audioSegmentStart !== initialFormData.audioSegmentStart
            || audioSegmentEnd !== initialFormData.audioSegmentEnd
            || !_.isEqual(audioBeatData, initialFormData.audioBeatData)
            || !_.isEqual(audioEventData, initialFormData.audioEventData))

        setDirty(isDirty);

    }, [smoothing, animationType, aspectRatio, audioBeatData, audioEventData, audioFilename, audioPresetData, audioSegmentEnd, audioSegmentStart, audioSyncMotionPreset, classicMotionPreset, duration30s, imagePrompt, imagePromptWeight, initialFormData, prompt, settingsOverrides, slowMo, style]);


    const estimateTempo = useCallback(async () => {
        if (!audioFilename) {
            setAudioBeatData(undefined);
            return false;
        }
        try {
            setIsEstimatingTempo(true);
            const startTime = window.performance.now();
            const remoteResult = await analyzeAudioFile(audioFilename);
            const remoteAnalysisTime = window.performance.now() - startTime;
            log.info(`Remote BPM analysis returned in ${remoteAnalysisTime}ms`)

            if (!remoteResult || !remoteResult.beat_positions || !isNumber(remoteResult.beat_positions[0]) || !isNumber(remoteResult.tempo)) {
                log.error("Remote analysis data not of the expected shape.", { remoteResult });
                throw new Error("Remote analysis data not of the expected shape.");
            }
            const remoteBeatData = {
                bpm: remoteResult.tempo,
                offset: remoteResult.beat_positions[0] % (60 / remoteResult.tempo)
            };

            const librosaEvents = remoteResult.onset_positions?.map((t, i) => remoteResult.onset_strengths?.[i] ? {t, strength: remoteResult.onset_strengths[i]} : undefined).filter(e => e != undefined) || [];
            setLibrosaEvents(librosaEvents);
            setAudioBeatData(remoteBeatData);

        } catch (error:unknown) {
            // TODO: better error handling here. Use a fallback BPM if we can't estimate it.
            log.error("Browser error: audio analysis failed", {error});
            setAudioBeatData(undefined);
            setUploadMessage({
                message: "Audio analysis failed. Try again or try another file.",
                type: 'failure',
                timestamp: Date.now(),
            });
        } finally {
            setIsEstimatingTempo(false);
        }
    }, [audioFilename, setAudioBeatData, setIsEstimatingTempo, setLibrosaEvents]);

    // A new audio file has been loaded and we have no beat data. Do automated detection!
    useEffect(() => {
        if (audioFilename && !audioBeatData) {
            void estimateTempo();
        } else if (!audioFilename)  {
            // Clear beat data if we have no audio file
            setAudioBeatData(undefined);
        }
    }, [audioFilename, estimateTempo, setAudioBeatData, audioBeatData]);


    useDebounceEffect( () => {
        if (isAnonymous) {
            return;
        }
        try {
            checkPrompt(prompt, style).then(({ lengthValidation, sfwValidation }) => {
                if (!sfwValidation) {
                    setPromptMessage('Please modify your prompt to reduce the likelihood of it being interpreted as NSFW. Deliberately generating NSFW content in Deforum Studio may result in your account being suspended. If you\'d like to generate NSFW content, consider running Deforum on your own hardware.');
                } else if (lengthValidation.neg_excessWords > 0) {
                    setPromptMessage('Your negative prompt is too long. Please remove a few words.');
                } else if (lengthValidation.pos_excessWords > 0) {
                    setPromptMessage('Your prompt is too long. Please remove a few words.');
                } else {
                    setPromptMessage('');
                }
            }).catch((error) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                log.warn("Error checking prompt length. ", { error });
            });
        } catch (error) {
            log.warn("Error checking prompt length. ", { error });
        }
    }, [prompt, style], {wait: 400, leading: true, trailing: true, maxWait: 400});



    //TODO move to atom derived state.
    useEffect(() => {
        const newCost = calculateCost({
            slowMo_v1: slowMo,
            imagePrompt: !!imagePrompt,
            duration30s: duration30s,
            isAudio: animationType === 'audio-reactive',
            audioDuration: _.round((audioSegmentEnd ?? 0) - (audioSegmentStart ?? 0), 2),
            smoothing: smoothing,
        });
        setCreditCost(newCost);
    }, [slowMo, imagePrompt, duration30s, audioUrl, audioSegmentEnd, audioSegmentStart, isAudioReactive, smoothing, animationType]);


    // Check whether settingsOverrides parsed correcly into derived atom. Emit message if not.
    useEffect(() => {
        if (!_.isEmpty(settingsOverrides) && !parsedSettingsOverride) {
                setSettingsOverridesMessage({ message: 'This looks like invalid JSON and will probably fail!', type: 'failure', timestamp: Date.now() });
        } else {
            setSettingsOverridesMessage(undefined);
        }
    }, [settingsOverrides, parsedSettingsOverride]);

    const preSubmitAudioAnalysis = useCallback( async () => {
        if (!isAudioReactive
            || !audioUrl
            || !isNumber(audioSegmentStart)
            || !isNumber(audioSegmentEnd)
            || !isNumber(audioBeatData?.bpm)
        ) {
            // We're not ready to perform analysis.
            return {};
        }

        const audioBuffer = await dataUrlToAudioBuffer(audioUrl);
        try {
            const { result, errors } = await doAllAudioAnalysis(new AudioContext(), audioBuffer, audioSegmentStart, audioSegmentEnd, audioBeatData?.bpm, fps, []);
            if (errors.length > 0) {
                const error = new Error("Errors detected in audio analysis");
                error.cause = errors;
                throw error;
            }
            return {
                ...framewiseDataSeries,
                ...result
            };
        } catch (error) {
            const shouldHalt = requiredAudioFields.includes('data_amp') || requiredAudioFields.includes('pan_amp');
            log.error("Browser error: client-side audio analysis failed", { error, shouldHalt });
            if (shouldHalt) {
                setUploadMessage({
                    message: `Extra audio analysis failed.`,
                    type: 'failure',
                    timestamp: Date.now(),
                });
                // We failed – halt here because the preset (or the overrides) needs the audio analysis data.
                throw Error("client-side audio analysis failed to compute fields required by preset.");
            }
            // We failed but proceed anyway without audio analysis data, because the preset doesn't need it.
            return {};
        }
    }, [isAudioReactive, audioUrl, audioSegmentStart, audioSegmentEnd, audioBeatData?.bpm, fps, framewiseDataSeries, requiredAudioFields]);


    const onSubmit = useCallback(async () => {
        if (submittingNewAnimation) {
            return;
        }

        try {
            if (style === '(Random)' || (audioSyncMotionPreset === '(Random)') || (classicMotionPreset === '(Random)')) {
                // We should no longer get to this point with unresolved Random styles or presets because under the covers we now pick the random selection immediately.
                throw new Error(`Unexpected unresolved random field: style=${style} audioSyncMotionPreset=${audioSyncMotionPreset} classicMotionPreset=${classicMotionPreset}`);
            }

            // Perform client-side audio analysis 
            const newFramewiseDataSeries = await preSubmitAudioAnalysis();
            unstable_batchedUpdates(() => {
                setSubmittingNewAnimation(true);
                setFramewiseDataSeries(newFramewiseDataSeries);
            });

            onSubmitStarted && onSubmitStarted();
            const result = await createAnimationFromButton({
                smoothing,
                audioBeatData,
                audioEventData,
                audioFilename,
                audioLocalFilename,
                audioPresetData,
                audioSegmentEnd,
                audioSegmentStart,
                audioSyncMotionPreset,
                classicMotionPreset,
                duration30s,
                imagePrompt,
                imagePromptWeight,
                isAudioReactive,
                prompt,
                settingsOverrides: settingsOverrides,
                slowMo,
                style,
                aspectRatio,
                useImagePrompt: !!imagePrompt,
                audioSegmentAnalysis: undefined,
                audioUrl : undefined, // Not used by submission processing, and causes inputsize buffer issues. TODO - need better distinction between data URL and remote URL.
                backendOverride,
                gpuFarmOverride,
                imagePromptThumbnail: undefined, // Not used by submission processing.
                imagePromptNsfwScore: imagePromptNsfwScore,
                isPromptHidden: false, // Not used by submission processing.
                framewiseDataSeries: newFramewiseDataSeries,
                audioProcessingConfigs: audioProcessingConfigs,
            }, basedOn);
            setSubmissionResult(result);
        } catch (e : unknown) {
            setSubmissionResult({
                userError: false,
                message: {
                    //@ts-expect-error - TODO error types
                    message: `An error occurred. Please try again. If the problem persists, please contact support. Info for support: ${e.message}`,
                    type: 'failure',
                    timestamp: Date.now(),
                }
            });
        } finally {
            setSubmittingNewAnimation(false);
        } 
        
    }, [submittingNewAnimation, style, audioSyncMotionPreset, classicMotionPreset, isAudioReactive, audioSegmentStart, audioSegmentEnd, audioBeatData, setSubmittingNewAnimation, setFramewiseDataSeries, onSubmitStarted, smoothing, audioEventData, audioFilename, audioLocalFilename, audioPresetData, duration30s, imagePrompt, imagePromptWeight, prompt, settingsOverrides, slowMo, aspectRatio, backendOverride, gpuFarmOverride, imagePromptNsfwScore, audioProcessingConfigs, basedOn, preSubmitAudioAnalysis]);


    useEffect(() => {
        if (submissionResult) {
            onSubmitResponded && onSubmitResponded(submissionResult);
        };
    }, [onSubmitResponded, submissionResult]); 

    const submissionResultMessage = useMemo(() => {
        if (!submissionResult) {
            return <></>;
        }
        if (submissionResult.message.type === 'success') {
            const success = submissionResult as AnimationCreationSuccess;
            return <div key={success.message.timestamp}>
                <Alert className='fade-out my-4 rounded-none text-neutral-400 bg-neutral-800' color={success.message.type}>
                    Animation generating! You are using {success.activeJobs}/{success.availableSlots} gen slots and have <PiCoinVerticalBold className='inline' />{success.creditBalance.toFixed(2)} remaining.
                </Alert>
            </div>
        } else {
            const failure = submissionResult as AnimationCreationFailure;
            if (failure.userError) {
                log.error("Internal error submitting animation returned to client: " + failure.message.message, { submissionResult });
            } else {
                log.warn("User error submitting animation returned to client: " + failure.message.message, { submissionResult });
            }
            return <div key={failure.message.timestamp}>
                <Alert className='fade-out my-4 rounded-none text-neutral-400 bg-neutral-800' color={failure.message.type}>
                    {failure.userError ? failure.message.message : `Something went wrong. We will investigate soon. Meanwhile feel free to try again. Info for developers: ${failure.message.message}`}
                </Alert>
            </div>
        }

    }, [submissionResult]);

    const adjustTextAreaHeight = (textArea: HTMLTextAreaElement) => {
        if (textArea) {
            textArea.style.height = 'inherit'; // Reset height to recalculate
            textArea.style.height = `${textArea.scrollHeight}px`; // Set new height
        }
    };

    useEffect(() => {
        adjustTextAreaHeight(document.getElementById('prompt') as HTMLTextAreaElement);
    }, []);

    const imagePromptWeightToWords = (weight: string) => {
        switch (weight) {
            case '0.3': return 'very low';
            case '0.45': return 'low';
            case '0.6': return 'medium';
            case '0.75': return 'high';
            case '0.9': return 'very high';
            default: return weight;
        }
    }

    function smoothingToWords(smoothing: number): import("react").ReactNode {
        switch (smoothing) {
            case 0: return 'off';
            case 1: return 'low';
            case 2: return 'medium';
            case 3: return 'high';
            case 4: return 'butter';
            default: return smoothing;
        }
    }    

    const canSubmit = useMemo(() => {
        const submitBlockReasons = [];
        if (!!promptMessage) {
            submitBlockReasons.push("Prompt is not suitable: " + promptMessage);
        }
        if (submittingNewAnimation) {
            submitBlockReasons.push("Animation request is in progress.");
        }
        if (uploadingImagePrompt || uploadingAudio) {
            submitBlockReasons.push("Audio or image upload is in progress.");
        }
        if (isAudioReactive && !audioUrl) {
            submitBlockReasons.push("Audio motion requires an audio file.");
        }
        if (isAudioReactive && audioUrl && !audioBeatData) {
            submitBlockReasons.push("Please wait for audio analysis to complete.");
        }
        if (isAudioReactive && (audioSegmentEnd ?? 0) - (audioSegmentStart ?? 0) < MIN_AUDIO_DURATION) {
            submitBlockReasons.push(`Please select at least ${MIN_AUDIO_DURATION}s of audio.`);
        }
        if (isAudioReactive && (audioSegmentEnd ?? 0) - (audioSegmentStart ?? 0) > maxAudioDurationForUser) {
            submitBlockReasons.push(`Please select at most ${maxAudioDurationForUser}s of audio.`);
        }
        if (isAudioReactive && requiredAudioFields?.includes('event') && !audioEventData?.length) {
            submitBlockReasons.push(`You must place at least 1 event on your audio track.`);
        }
        if (!imagePrompt && _.isEmpty(prompt)) {
            submitBlockReasons.push("You must provide a prompt, an image prompt or both.");
        }

        if (creditCost<=0) {
            submitBlockReasons.push("No credit cost calculated; please check your form data.");
        }

        if (is_admin && !_.isEmpty(settingsOverrides) && !parsedSettingsOverride) {
            submitBlockReasons.push("Hi admin, please fix your settings overrides (they are not parsing).");
        }

        if (submitBlockReasons.length > 0) {
            return { canSubmit: false, reasons: submitBlockReasons };
        } else {
            return { canSubmit: true, reasons: [] };
        }

    }, [audioBeatData, audioEventData?.length, audioSegmentEnd, audioSegmentStart, audioUrl, creditCost, imagePrompt, isAudioReactive, is_admin, maxAudioDurationForUser, parsedSettingsOverride, prompt, promptMessage, requiredAudioFields, settingsOverrides, submittingNewAnimation, uploadingAudio, uploadingImagePrompt]);

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    return <div className='w-full h-full flex flex-col justify-between bg-slate-00'>

        <div className="flex flex-wrap">
            {/* Column 1 */}
            <div className="w-full md:w-1/2 md:pr-0">
                <div className='flex flex-row w-full items-start'>
                    <div className='flex flex-col w-full mx-0'>

                        <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Guide the animation with a descriptive text caption.">
                            <Label htmlFor="prompt" className="mb-1 block text-neutral-700 dark:text-neutral-400">Text Prompt <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/></Label>
                        </Tooltip>
                        <Textarea
                            className="rounded-none bg-white border-neutral-700 dark:bg-black dark:border-neutral-400 focus:ring-blue-500/35 placeholder-neutral-700 dark:placeholder-neutral-400"
                            id="prompt"
                            name="prompt"
                            required={!imagePrompt}
                            disabled={isAnonymous||submittingNewAnimation}
                            value={prompt}
                            onChange={(e) => {
                                adjustTextAreaHeight(e.target);
                                setPrompt(e.target.value);
                            }}
                            placeholder='Type your prompt here'
                        />
                        {(prompt === '' && initialFormData.isPromptHidden) && <div className='text-xs font-thin'>The creator of this animation chose to keep the prompt private.</div>}
                        {promptMessage && <Alert className='mt-2 rounded-none' color={'warning'}>{promptMessage}</Alert>}
                        <div className='flex flex-col w-full'>
                            <div className="block custom-textfield overflow-visible w-full">
                                <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Add style keywords to the text prompt.">
                                    <Label htmlFor="style" className="mb-1 mt-4 block text-neutral-700 dark:text-neutral-400">Prompt Style <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/></Label>
                                </Tooltip>
                                <FilterableListbox
                                    disabled={isAnonymous||submittingNewAnimation}
                                    name="style"
                                    value={styleWasRandomlySelected ?  '(Random)' : style}
                                    onChange={(newValue) => {
                                        setStyleWasRandomlySelected(false);
                                        setStyle(newValue || '(Random)');
                                    }}
                                    options={styleList}
                                />
                            </div>
                        </div>
                        <div className={classNames('flex flex-row items-center gap-2 mt-4 mb-2')}>
                            <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Upload an image to guide the animation.">
                                <Label htmlFor="prompt" className={classNames("block text-neutral-700 dark:text-neutral-400")}>Image Prompt (Optional) <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/></Label>
                            </Tooltip>
                        </div>
                    </div>

                </div>
                <div className={classNames("mb-2 flex items-center justify-center border border-neutral-700  dark:border-neutral-400  dark:bg-black bg-white",
                    `${imagePromptThumbnail ? 'cursor-default' : 'cursor-pointer'}`, 'min-h-44 h-44 w-full border-neutral-700  dark:border-neutral-400 ', animateStyles['container'])}>
                    {<Dropzone
                        onDrop={onDropImage}
                        noClick={!!imagePromptThumbnail}
                        multiple={false}
                        maxFiles={1}
                        maxSize={4.5 * 1024 * 1024}
                        accept={{
                            'image/jpeg': ['.jpeg', '.jpg'],
                            'image/png': ['.png'],
                            'image/webp': ['.webp'],
                        }}
                        onDropAccepted={() => setUploadMessage(undefined)}
                        onDropRejected={() => setUploadMessage({ message: "Couldn't upload image. Ensure it's under 4.5 and is a supported format.", type: 'failure', timestamp: Date.now() })}
                        validator={(item) => {
                            const { name: fileName } = item;
                            if (fileName) {
                                const ext = fileName.split('.').pop();
                                if (ext?.toLowerCase() === 'jfif') {
                                    return { code: 'file-invalid-type', message: '.jfif is not supported - try converting to jpg' }
                                }
                            }
                            return null
                        }}
                        noKeyboard={true}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <section className='relative w-full h-full'>
                                <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <div className="relative h-full w-full flex flex-col items-center justify-center">
                                        
                                        {imagePromptThumbnail
                                            ? <ModalImage
                                                hideDownload
                                                hideZoom
                                                className='max-h-44 h-44 object-contain'
                                                height={"80px"}
                                                small={imagePromptThumbnail}
                                                medium={imagePromptThumbnail}
                                                large={imagePromptThumbnail}
                                                alt="Image prompt" />
                                            : <div className='py-4 flex flex-col items-center justify-center overflow-hidden h-44'>
                                                <LuImagePlus className='w-12 h-12 text-neutral-700 dark:text-neutral-400' />
                                                <p className='text-neutral-700 dark:text-neutral-400 px-4 text-center'>Drop an image here, or click to browse. JPG, PNG or WEBP, 4.5MB max.</p>
                                            </div>
                                        }
                                        {imagePromptThumbnail &&
                                            <Button
                                                className={classNames('absolute h-6 w-[64px] items-center justify-center -top-8 right-0 m-1 rounded-none bg-red-00 text-white border opacity-85 hover:opacity-100')}
                                                disabled={uploadingAudio}
                                                onClick={(e: any) => {
                                                    unstable_batchedUpdates(() => {
                                                        setUploadMessage(undefined);
                                                        setImagePromptThumbnail(undefined);
                                                        setImagePrompt('');
                                                        setImagePromptNsfwScore(undefined);
                                                        setImagePromptWeight('0.6');
                                                    });
                                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                                    e.stopPropagation();
                                                }}>
                                                <span className='flex flow-row items-center text-neutral-700 dark:text-neutral-400'>[clear<AiFillCloseSquare className='ml-1' />]</span>
                                            </Button>}
                                        {uploadingImagePrompt && <div className='absolute bottom-0 w-full m-0 text-center'>
                                            {<Alert className='rounded-none px-2 text-xs my-0 py-1 bg-opacity-90 text-center text-black z-20' color='info'><Spinner className='w-3 h-3 mr-2' />Preparing image... </Alert>}
                                        </div>}
                                        {uploadMessage && uploadMessage.message.toLowerCase().includes('image') &&
                                            <div
                                                key={uploadMessage.timestamp}
                                                className={classNames('absolute bottom-0 w-full m-0 text-center  z-30', uploadMessage.type === 'success' ? 'fade-out-3s' : '')}>
                                                <Alert className='rounded-none px-2 text-xs my-0 py-1 bg-opacity-90 text-center text-black' color={uploadMessage.type}>
                                                    {uploadMessage.type === 'success' ? <FaCheckCircle className='inline w-2 h-2' /> : <FaExclamationTriangle className='inline w-2 h-2' />}&nbsp;{uploadMessage.message}</Alert>
                                            </div>}
                                        
                                    </div>
                                </div>
                            </section>
                        )}
                    </Dropzone>}
                </div>
                {<>
                    <Label htmlFor="steps-range" className="mt-4 mb-1 block text-neutral-700 dark:text-neutral-400">Image influence: <span className='font-light'>{imagePrompt ? imagePromptWeightToWords(imagePromptWeight) : 'n/a'}</span></Label>
                    <div className="flex gap-2 items-center justify-between w-full">
                        <div className='block text-neutral-700 dark:text-neutral-400 text-xs font-light'>Text</div>
                        <input
                            id="steps-range"
                            type="range"
                            min={0.3}
                            max={0.9}
                            step={0.15}
                            value={imagePromptWeight}
                            onChange={(e) => setImagePromptWeight(e.target.value)}
                            className="w-full h-2 bg-blue-400 rounded-none appearance-none cursor-pointer [&::-webkit-slider-thumb]:rounded-none" />
                        <div className='block text-neutral-700 dark:text-neutral-400 text-xs font-light'>Image</div>
                        {is_admin && imagePromptNsfwScore && <div className="font-light text-xs ">(Admin only) nsfw score: {imagePromptNsfwScore}</div>}

                    </div>
                </>}
            </div>

            {/* Column 2 */}
            <div className="w-full md:w-1/2 md:pl-2">
                {enableAudioSync &&  <div className='flex flex-col'>
                    <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Switch between classic motion presets and audio-sync motion presets." className="z-20">
                        <Label htmlFor="prompt" className="mb-1 block text-neutral-700 dark:text-neutral-400">Motion Style <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/></Label>
                    </Tooltip>
                    <MotionTypeSelector />
                </div>}
                <div className="block custom-textfield overflow-visible w-full">
                    <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Motion presets are pre-configured camera motions and animation styles." className="z-20">
                        <Label className={classNames("mb-1 block text-neutral-700 dark:text-neutral-400",enableAudioSync ? 'mt-4' : '')}>
                        { (enableAudioSync && isAudioReactive) ? 'Audio Sync' : enableAudioSync ? 'Classic' : '' } Motion Preset <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/>
                        </Label>
                    </Tooltip>
                    {(enableAudioSync && isAudioReactive)
                        ? <FilterableListbox
                            disabled={isAnonymous||submittingNewAnimation}
                            name="audioSyncPreset"
                            value={audioSyncedPresetWasRandomlySelected ?  '(Random)' : audioSyncMotionPreset}
                            onChange={(newValue) => {
                                setAudioSyncedPresetWasRandomlySelected(newValue==='(Random)');
                                setAudioSyncMotionPreset(newValue || '(Random)');
                            }}
                            options={audioSyncPresetList as string[]}
                        />
                        : <FilterableListbox
                            disabled={isAnonymous||submittingNewAnimation}
                            name="classicPreset"
                            value={classicPresetWasRandomlySelected ?  '(Random)' : classicMotionPreset}
                            onChange={(newValue) => {
                                setClassicPresetWasRandomlySelected(newValue==='(Random)');
                                setClassicMotionPreset(newValue || '(Random)');
                            }}
                            options={classicPresetList as string[]}
                        />
                    }
                </div>
                {(!isAudioReactive || !enableAudioSync) && <div className="flex flex-col">
                    <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Control the duration of your animation." className="z-20">
                        <Label className={classNames("mb-1 mt-4  block text-neutral-700 dark:text-neutral-400",enableAudioSync ? 'mt-4' : '')}>
                            Animation Length <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/>
                        </Label>
                    </Tooltip>
                    <div className="flex justify-between items-center w-full flex-wrap">
                        <Tooltip arrow={false} style="light" placement="bottom" content="Double the length of your animation with a slow-motion effect.">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    name="slowMo"
                                    id="slowMo"
                                    className='w-6 h-6 rounded-none border text-neutral-800 bg-white dark:bg-neutral-700 border-black dark:border-white focus:ring-black dark:focus:ring-white  dark:ring-offset-gray-500 focus:ring-2'
                                    onChange={(e) => setSlowMo(e.target.checked)}
                                    checked={slowMo}
                                    disabled={isAnonymous||submittingNewAnimation}
                                />
                                <Label htmlFor="slowMo" className='text-black dark:text-white font-light'>Slow-motion</Label>
                            </div>
                        </Tooltip>
                        <Tooltip arrow={false} style="light" placement="bottom" content="Change animation duration.">
                            <div className='flex items-center justify-center gap-0'>
                                <span className={classNames('font-light text-sm')}>Duration: <span className={classNames(!duration30s ? 'text-black dark:text-white' : 'text-neutral-400')}> {slowMo ? '30s' : '15s'}</span></span>
                                <Switch
                                    checked={duration30s}
                                    name="duration30s"
                                    onClick={() => { setDuration30s((current) => !current) }}
                                    className="ml-3 mr-4 relative inline-flex h-2 w-8 items-center rounded-none ui-checked:bg-blue-400 ui-not-checked:bg-neutral-400 ui-not-checked:dark:bg-neutral-600">
                                    <span className="inline-block h-5 w-5 transform rounded-sm bg-neutral-500 dark:bg-neutral-400 transition ui-checked:translate-x-6 ui-not-checked:-translate-x-1" />
                                </Switch>
                                <span className={classNames('font-light text-sm', duration30s ? 'text-black dark:text-white' : 'text-neutral-400')}>{slowMo ? '60s' : '30s'}</span>
                            </div>
                        </Tooltip>
                    </div>
                </div>}

                {(isAudioReactive) && <Tooltip trigger="click" arrow={false} style="light" placement="bottom" content="Upload an audio file to guide the animation motion.">
                    <Label htmlFor="style" className="mb-2 mt-4 block text-neutral-700 dark:text-neutral-400">Audio File Upload <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/></Label>
                </Tooltip>}
                <div className={classNames("relative flex flex-col items-center justify-center border border-neutral-700  dark:border-neutral-400  dark:bg-black bg-white",
                    `${audioUrl ? 'cursor-default' : 'cursor-pointer'}`,
                    isAudioReactive ? 'min-h-44 h-44 mt w-full border-neutral-700  dark:border-neutral-400 ' : 'mb-0 max-h-0 min-h-0 md:max-w-0 border-none', animateStyles['container'])}>
                    {(isAudioReactive) && <Dropzone
                        onDrop={(e) => void onDropAudio(e)}
                        noClick={!!audioUrl}
                        multiple={false}
                        maxFiles={1}
                        maxSize={20 * 1024 * 1024}
                        accept={{
                            'audio/mpeg': ['.mp3'],
                        }}
                        onDropAccepted={() => setUploadMessage(undefined)}
                        onDropRejected={() => setUploadMessage({ message: "Couldn't upload audio. Ensure it's under 20MB and is a supported format.", type: 'failure', timestamp: Date.now() })}
                        noKeyboard={true}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <section className='flex flex-col w-full h-full justify-between bg-blue-00'>
                                <div {...getRootProps()}>
                                    <input {...getInputProps()} />

                                    <div className="h-full w-full flex flex-col justify-end">
                                        {audioUrl
                                            ? <div className='flex flex-col items-center overflow-hidden h-44 text-xs'>
                                                        <LuMusic className='w-12 h-12 text-neutral-700 dark:text-neutral-400 my-2' size={96} />
                                                        <div className='font-bold'>Audio: { _.truncate(audioLocalFilename, {length:30})}</div>
                                                        <div>Duration: {audioTotalDuration.toFixed(2)}s</div>
                                                        {(isNumber(audioSegmentStart) && isNumber(audioSegmentEnd))
                                                            && <div className='text-neutral-700 dark:text-neutral-400  text-xs'>
                                                                Selected: {(audioSegmentEnd - audioSegmentStart).toFixed(2)}s ({audioSegmentStart.toFixed(2)} - {audioSegmentEnd.toFixed(2)})
                                                            </div>}
                                                        <div>
                                                            {audioBeatData
                                                                ? `~${audioBeatData.bpm.toFixed(0)} bpm offset by ${audioBeatData.offset.toFixed(1)}s (see below to edit)`
                                                                : 'calculating bpm...'
                                                            }
                                                        </div>
                                                        {isMobile && <div>
                                                            <Button
                                                                className='rounded-none bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white px-1 my-2 hover:scale-x-110 hover:ease-linear hover:duration-300'
                                                                size={'sm'}
                                                                onClick={() => {
                                                                    if (isMobileAudioEditorOpen) {
                                                                        // Closing: pop history state
                                                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                                                        if (window.history.state && window.history.state.modal) {
                                                                            window.history.back();
                                                                        }
                                                                    } else {
                                                                        // Opening: store history state.
                                                                        window.history.pushState({ modal: true }, '');
                                                                    }
                                                                    
                                                                    setIsMobileAudioEditorOpen(!isMobileAudioEditorOpen);
                                                                }}
                                                                >
                                                                <div className='flex flex-row items-center gap-x-2 '>
                                                                    <PiWaveformThin />
                                                                    <span>{isMobileAudioEditorOpen?'Close':'Open'} waveform editor</span>
                                                                </div>
                                                            </Button>
                                                        </div>}
                                            </div>

                                            : <div className='flex flex-col items-center justify-center overflow-hidden h-44'>
                                                <LuMusic className='w-12 h-12 text-neutral-400 dark:text-neutral-700 mb-4' size={96} />
                                                <p className='text-neutral-700 dark:text-neutral-400 px-4 text-center'>Drop your mp3 file here, or click to browse.</p>
                                            </div>
                                        }
                                        {audioUrl && <Button
                                            className={classNames('absolute h-6 w-[64px] items-center justify-center -top-8 right-0 m-1 rounded-none bg-red-00 text-white border opacity-85 hover:opacity-100')}
                                            disabled={uploadingAudio||submittingNewAnimation}
                                            onClick={(e: any) => {
                                                unstable_batchedUpdates(() => {
                                                    setAudioBeatData(undefined);
                                                    setAudioEventData(undefined);
                                                    setAudioSegmentStart(0);
                                                    setAudioSegmentEnd(undefined);
                                                    setUploadMessage(undefined);
                                                    setAudioUrl(undefined);
                                                    setAudioFilename(undefined);
                                                });
                                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                                e.stopPropagation();
                                            }}> 
                                            <span className='flex flow-row items-center text-neutral-700 dark:text-neutral-400'>[clear<AiFillCloseSquare className='ml-1' />]</span>
                                        </Button>}
                                        {uploadingAudio && <div className='absolute bottom-0 w-full m-0 text-center'>
                                            <Alert className='rounded-none px-2 text-xs my-0 py-1 bg-opacity-90 text-center text-black z-30' color='info'><Spinner className='w-3 h-3 mr-2' />Preparing audio... </Alert>
                                        </div>}
                                        {uploadMessage && uploadMessage.message.toLowerCase().includes('audio') &&
                                            <div
                                                key={uploadMessage.timestamp}
                                                className={classNames('absolute bottom-0 w-full m-0 text-center z-30', uploadMessage.type === 'success' ? 'fade-out-3s' : '')}>
                                                <Alert className='rounded-none px-2 text-xs my-0 py-1 bg-opacity-90 text-center text-black' color={uploadMessage.type}>
                                                    {uploadMessage.type === 'success' ? <FaCheckCircle className='inline w-2 h-2' /> : <FaExclamationTriangle className='inline w-2 h-2' />}&nbsp;{uploadMessage.message}</Alert>
                                            </div>}
                                    </div>
                                </div>
                            </section>
                        )}
                    </Dropzone>}                    
                </div>
                {<>
                    <Tooltip arrow={false} trigger='click' style="light" placement="bottom" content="Improve video coherence with an additional temporal pass (using AnimateDiff)." className="z-20">
                        <Label htmlFor="style" className="mt-4 mb-1 block text-neutral-700 dark:text-neutral-400">Smoothing: <AiOutlineInfoCircle className="inline-block  mr-2 text-xs"/> <span className='font-light'>{smoothingToWords(smoothing)}</span></Label>
                    </Tooltip>                        
                    <div className="flex gap-2 items-center justify-between w-full">
                        <div className='block text-neutral-700 dark:text-neutral-400 text-xs font-light'>Off</div>
                        <input
                            id="steps-range"
                            type="range"
                            min={0}
                            max={4}
                            step={1}
                            value={smoothing}
                            onChange={(e) => setSmoothing(parseInt(e.target.value))}
                            className="w-full h-2 bg-blue-400 rounded-none appearance-none cursor-pointer [&::-webkit-slider-thumb]:rounded-none" />
                        <div className='block text-neutral-700 dark:text-neutral-400 text-xs font-light'>Max</div>
                    </div>
                </>}
            </div>
        </div>

        {!isAnonymous && <div className='mt-6 flex md:flex-row xs:flex-col items-center gap-4 w-full justify-between self-end'>
            <div className='flex flex-col w-full mb-4 md:mb-4 bg-red-00'>
                <Label htmlFor="ar" className="mb-1 block text-neutral-700 dark:text-neutral-400">Aspect ratio</Label>
                <AspectRatioToggleWithAtom />
            </div>
            <div className='flex flex-row w-full justify-between'>
                <div className='justify-self-end self-center h-full'><MotionPresetsTip /></div>
                <div className='flex flex-row items-center justify-center gap-4'>
                    <Tooltip hidden={canSubmit.canSubmit} arrow={false} style="light" placement="bottom" content={canSubmit.reasons.join(" \n")} className={classNames('z-50', canSubmit.canSubmit ? 'hidden' : '')}>
                        <Button
                            className='rounded-none bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white px-1 py-1 hover:scale-x-110 hover:ease-linear hover:duration-300'
                            disabled={!canSubmit.canSubmit}
                            onClick={() => void onSubmit()}
                            >
                            {(creditCost<0) //TODO: used as a proxy for page load, should be a separate state
                                ? <><StyledSpinner size={'sm'} className="inline mr-1" /> LOADING</>
                                : (dirty || (prompt == '' && !imagePrompt))
                                    ? <div className='text-sm font-bold mx-1 whitespace-nowrap w-full flex flex-row justify-between'>
                                        <>CREATE</>{<div className='flex flex-row items-center mx-2'><PiCoinVerticalBold className='font-extralight text-lg inline' />&nbsp;{creditCost.toFixed(2)}</div>}
                                    </div>
                                    : <div className='text-sm font-bold mx-1 whitespace-nowrap w-full flex flex-row justify-between'>
                                        <><CiRedo className="inline mr-1" />RE-RUN</>{<div className='flex flex-row items-center mx-2'><PiCoinVerticalBold className='font-extralight text-lg inline' />&nbsp;{creditCost.toFixed(2)}</div>}
                                    </div>
                            }
                        </Button>
                    </Tooltip>
                </div>
            </div>

        </div>}
        {is_admin && <>
            <Label htmlFor="ar" className="mt-4 mb-1 block text-red-500 dark:text-red-500" onClick={() => setShowSettingsOverrides(!showSettingsOverrides)} >Admin only extra options (click to show/hide)</Label>
            {showSettingsOverrides && <>
                <Label htmlFor="ar" className="mb-1 block text-neutral-700 dark:text-neutral-400">Settings file overrides</Label>
                <Textarea
                    className="rounded-none bg-white dark:bg-black  border-neutral-700  dark:border-neutral-400  focus:ring-blue-500/35 font-mono text-xs"
                    id="settingsOverrides"
                    name="settingsOverrides"
                    disabled={!is_admin||submittingNewAnimation}
                    value={settingsOverrides}
                    onChange={(e) => setSettingsOverrides(e.target.value)}
                    placeholder='Deforum settings overrides as JSON. Example: {"color_coherence": "None"}. You can also paste in a full settings file.'
                />
                {settingsOverridesMessage && <Alert key={settingsOverridesMessage.timestamp} color={settingsOverridesMessage.type}>{settingsOverridesMessage.message}</Alert>}
                {isAudioReactive && <>
                    <Label htmlFor="ar" className="mt-2 mb-1 block text-neutral-700 dark:text-neutral-400">ProjectM preset</Label>
                    <Textarea
                        className="rounded-none bg-white dark:bg-black  border-neutral-700  dark:border-neutral-400  focus:ring-blue-500/35  font-mono text-xs"
                        id="audioPresetData"
                        name="audioPresetData"
                        disabled={!is_admin||submittingNewAnimation}
                        value={audioPresetData}
                        onChange={(e) => {
                            setAudioPresetData(e.target.value);
                        }}
                        placeholder='Paste in a full projectM preset here'
                    />
                </>}
                <div className='mt-2 w-full grid grid-cols-2 space-x-4' >
                    <div>
                        <Label htmlFor="ar" className="mb-1 block text-neutral-700 dark:text-neutral-400">Gpu Farm</Label>
                        <Dropdown
                            label=""
                            className="rounded-none bg-white dark:bg-black  border-neutral-700  dark:border-neutral-400  focus:ring-blue-500/35"
                            renderTrigger={() => <Button
                                className="w-full rounded-none bg-neutral-700 text-white dark:bg-neutral-400 dark:text-black  hover:scale-x-110 hover:ease-linear hover:duration-300">
                                {_.isEmpty(gpuFarmOverride) ? `Default (runpod:${GpuFarm.probabilities.runpod}; replicate:${GpuFarm.probabilities.replicate})` : gpuFarmOverride}
                                <HiOutlineChevronUpDown className="inline ml-1 w-6 h-6" />
                            </Button>}
                        >
                            <Dropdown.Item onClick={() => { setGpuFarmOverride(undefined); setBackendOverride(undefined); }} >{`Default (runpod:${GpuFarm.probabilities.runpod}; replicate:${GpuFarm.probabilities.replicate})`}</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => { setGpuFarmOverride('dummy'); setBackendOverride(undefined); }} >dummy</Dropdown.Item>
                            <Dropdown.Item onClick={() => { setGpuFarmOverride('replicate'); setBackendOverride(undefined); }} >replicate</Dropdown.Item>
                            <Dropdown.Item onClick={() => { setGpuFarmOverride('runpod'); }} >runpod</Dropdown.Item>
                        </Dropdown>
                    </div>
                    <div>
                        <Label htmlFor="ar" className="mb-1 block text-neutral-700 dark:text-neutral-400">Backend</Label>
                        <Dropdown
                            label=""
                            className="rounded-none bg-white dark:bg-black  border-neutral-700  dark:border-neutral-400  focus:ring-blue-500/35"
                            renderTrigger={() => <Button
                                className="w-full rounded-none bg-neutral-700 text-white dark:bg-neutral-400 dark:text-black  hover:scale-x-110 hover:ease-linear hover:duration-300">
                                {_.isEmpty(backendOverride) ? `Default (auto-select)` : backendOverride}
                                <HiOutlineChevronUpDown className="inline ml-1 w-6 h-6" />
                            </Button>}
                        >
                            <Dropdown.Item onClick={() => setBackendOverride(undefined)}>{`Default (auto-select)`}</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => { setBackendOverride('animation-toolkit'); setGpuFarmOverride('runpod'); }}>animation-toolkit</Dropdown.Item>
                            <Dropdown.Item onClick={() => { setBackendOverride('forge'); setGpuFarmOverride('runpod'); }}>forge</Dropdown.Item>
                            <Dropdown.Item onClick={() => { setBackendOverride('a1111'); }}>a1111</Dropdown.Item>
                        </Dropdown>
                        <input hidden={true} type="text" id="backendOverride" name="backendOverride" value={backendOverride} onChange={() => { }} />
                    </div>
                </div>
            </>
            }
        </>}
        {submissionResultMessage}
    </div>
}
